import pluralize from 'pluralize';

import { FileStatus } from '@air/feature-file-status/constants';
import { type FileTrackingItemCounts } from '@air/feature-file-status/ui';

import { formatEstimatedCompletionTime } from './formatEstimatedCompletionTime';
import { formatUploadSpeedText } from './formatUploadSpeedText';

export type GetUploadHeaderTitlesArgs = {
  counts: FileTrackingItemCounts;
  estimatedCompletionTime: number | null;
  summaryLocation: string | undefined;
  isOnline: boolean;
  speed: number | null;
};

export type GetUploadHeaderTitlesResult = {
  [key in FileStatus]: {
    title: string;
    subtitle?: string;
  };
};

export const getUploadHeaderTitles = ({
  counts,
  estimatedCompletionTime,
  summaryLocation,
  isOnline,
  speed,
}: GetUploadHeaderTitlesArgs): GetUploadHeaderTitlesResult => {
  const { formattedCompletionTime, showSpeed } = formatEstimatedCompletionTime(estimatedCompletionTime);
  const formattedSpeed = formatUploadSpeedText(speed);

  const pausedTitle = {
    subtitle: isOnline ? `${pluralize('items', counts.pausedCount, true)} remaining` : 'No internet connection',
    title: `Upload paused`,
  };

  const getProgressingSubtitle = () => {
    if (!formattedSpeed) return '';
    if (showSpeed) return `${formattedCompletionTime} · ${formattedSpeed}`;
    return formattedCompletionTime ?? '';
  };

  const progressingTitle = {
    subtitle: getProgressingSubtitle(),
    title: `Uploading ${pluralize('items', counts.progressingCount, true)}`,
  };

  const erroredTitle = {
    subtitle: `${pluralize('items', counts.erroredCount, true)} failed`,
    title: `Upload incomplete`,
  };

  const completedSubtitle = `${pluralize('items', counts.completedCount, true)}`;

  const completedTitle = {
    subtitle: summaryLocation ? `${completedSubtitle} to ${summaryLocation}` : completedSubtitle,
    title: `Upload complete`,
  };

  const abortedTitle = {
    subtitle: `${pluralize('items', counts.abortedCount, true)} cancelled`,
    title: `Upload cancelled`,
  };

  const stoppedTitle = {
    subtitle: `${pluralize('items', counts.stoppedCount, true)} stopped`,
    title: `Upload stopped`,
  };

  const preparingTitle = {
    subtitle: `preparing to upload`,
    title: `Preparing`,
  };

  return {
    [FileStatus.errored]: erroredTitle,
    [FileStatus.paused]: pausedTitle,
    [FileStatus.progressing]: progressingTitle,
    [FileStatus.completed]: completedTitle,
    [FileStatus.aborted]: abortedTitle,
    [FileStatus.stopped]: stoppedTitle,
    [FileStatus.preparing]: preparingTitle,
  };
};
